/**
 * Test terrain for M0 (spec §11, "Vite app rendering a test terrain").
 *
 * A seeded fBm heightfield, so what you see is reproducible and a screenshot diff
 * means something (spec §10.4). It is generated here, in the front end, only
 * because M0 has no level loader on the TypeScript side yet; from M1 the terrain
 * comes out of `hydro-sim` as a field view and this module keeps only the mesh
 * building.
 *
 * Nothing here is physics. Heights are metres, cell size is metres (spec §4.2).
 */

/** Cell size in metres (spec §4.2). */
export const DX_M = 2;

/** A square heightfield, row-major, in metres. */
export interface Heightfield {
  /** Cells per side. */
  readonly n: number;
  /** Cell size in metres. */
  readonly dxM: number;
  /** Elevations in metres, length `n * n`. */
  readonly zM: Float32Array;
  /** Lowest elevation present (m). */
  readonly minM: number;
  /** Highest elevation present (m). */
  readonly maxM: number;
}

/**
 * A small deterministic PRNG (mulberry32). The weather and the physics are seeded
 * in Rust with `rand_chacha` (spec §9.3); this is only for the placeholder mesh,
 * but it is still seeded so the view is reproducible.
 */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Smooth interpolation used between lattice points. */
function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

/** One octave of value noise on a `grid x grid` lattice, sampled at (u, v) in [0,1). */
function valueNoise(lattice: Float32Array, grid: number, u: number, v: number): number {
  const x = u * grid;
  const y = v * grid;
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = smoothstep(x - x0);
  const fy = smoothstep(y - y0);
  const at = (ix: number, iy: number): number =>
    lattice[(((iy % grid) + grid) % grid) * grid + (((ix % grid) + grid) % grid)];
  const top = at(x0, y0) * (1 - fx) + at(x0 + 1, y0) * fx;
  const bottom = at(x0, y0 + 1) * (1 - fx) + at(x0 + 1, y0 + 1) * fx;
  return top * (1 - fy) + bottom * fy;
}

/** Options for {@link generateTestTerrain}. */
export interface TerrainOptions {
  /** Cells per side. */
  readonly n?: number;
  /** PRNG seed. */
  readonly seed?: number;
  /** Elevation of the valley floor (m). */
  readonly baseM?: number;
  /** Elevation range from floor to ridge (m). */
  readonly reliefM?: number;
  /** Cell size (m). */
  readonly dxM?: number;
}

/**
 * Generates a valley: fBm noise plus a shallow cross-valley trough, so there is an
 * obvious low line for water to find once the solver exists.
 */
export function generateTestTerrain(options: TerrainOptions = {}): Heightfield {
  const n = options.n ?? 256;
  const seed = options.seed ?? 1337;
  const baseM = options.baseM ?? 100;
  const reliefM = options.reliefM ?? 40;
  const dxM = options.dxM ?? DX_M;

  const rand = mulberry32(seed);
  const octaves = [4, 8, 16, 32, 64, 128];
  const lattices = octaves.map((grid) => {
    const l = new Float32Array(grid * grid);
    for (let i = 0; i < l.length; i += 1) l[i] = rand();
    return l;
  });

  const zM = new Float32Array(n * n);
  let minM = Number.POSITIVE_INFINITY;
  let maxM = Number.NEGATIVE_INFINITY;

  for (let y = 0; y < n; y += 1) {
    for (let x = 0; x < n; x += 1) {
      const u = x / n;
      const v = y / n;

      // Fractional Brownian motion: halve the amplitude as the frequency doubles.
      let noise = 0;
      let amplitude = 0.5;
      let norm = 0;
      for (let o = 0; o < octaves.length; o += 1) {
        noise += valueNoise(lattices[o], octaves[o], u, v) * amplitude;
        norm += amplitude;
        amplitude *= 0.5;
      }
      noise /= norm;

      // A trough running north-south, deepest at u = 0.5.
      const acrossValley = (u - 0.5) * 2;
      const trough = 1 - Math.exp(-(acrossValley * acrossValley) / 0.08);

      // A gentle downstream slope, so water would run south: 0.2 % over the map.
      const downstream = -0.002 * v * n * dxM;

      const z = baseM + reliefM * (0.4 * noise + 0.6 * trough) + downstream;
      zM[y * n + x] = z;
      if (z < minM) minM = z;
      if (z > maxM) maxM = z;
    }
  }

  return { n, dxM, zM, minM, maxM };
}

/** Elevation at a cell, clamped to the field (m). */
export function sampleM(field: Heightfield, x: number, y: number): number {
  const cx = Math.min(field.n - 1, Math.max(0, x | 0));
  const cy = Math.min(field.n - 1, Math.max(0, y | 0));
  return field.zM[cy * field.n + cx];
}

/**
 * Downhill gradient magnitude at a cell, dimensionless (m/m).
 *
 * Central differences over the cell size. This is the slope the grade tool and the
 * permissible-velocity warnings will read (spec §5.9, §6.3).
 */
export function slopeAt(field: Heightfield, x: number, y: number): number {
  const dzdx = (sampleM(field, x + 1, y) - sampleM(field, x - 1, y)) / (2 * field.dxM);
  const dzdy = (sampleM(field, x, y + 1) - sampleM(field, x, y - 1)) / (2 * field.dxM);
  return Math.hypot(dzdx, dzdy);
}
